import React, { forwardRef } from "react";
import styled from "@emotion/styled";
import { NavLink, useLocation } from "react-router-dom";
import { rgba, darken } from "polished";
import { useTheme } from "@mui/material/styles";
import { Sliders, Framer, Aperture, Database, Users, Archive, CreditCard } from "react-feather";

import { Chip, Collapse, ListItemButton, ListItemText, Box } from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  AccountBalanceWallet,
  CardMembership,
  Storefront,
  Inventory,
  Business,
  Timeline,
  Email,
} from "@mui/icons-material";

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref} style={{ flexGrow: 1, display: "flex" }}>
    <NavLink {...props} style={{ textDecoration: "none", color: "inherit", width: "100%" }} />
  </div>
));

const iconGenerate = (icon, isPrimaryHighlight) => {
  const sx = isPrimaryHighlight ? { color: "inherit" } : {};
  switch (icon) {
    case "Sliders":
      return <Sliders style={sx} />;
    case "Aperture":
      return <Aperture style={sx} />;
    case "AccountBalanceWalletIcon":
      return <AccountBalanceWallet sx={sx} />;
    case "Framer":
      return <Framer style={sx} />;
    case "Database":
      return <Database style={sx} />;
    case "Users":
      return <Users style={sx} />;
    case "Archive":
      return <Archive style={sx} />;
    case "CreditCard":
      return <CreditCard style={sx} />;
    case "CardMembershipIcon":
      return <CardMembership sx={sx} />;
    case "StorefrontIcon":
      return <Storefront sx={sx} />;
    case "InventoryIcon":
      return <Inventory sx={sx} />;
    case "BusinessIcon":
      return <Business sx={sx} />;
    case "Timeline":
      return <Timeline sx={sx} />;
    case "Email":
      return <Email sx={sx} />;
    default:
      return <Sliders style={sx} />;
  }
};

const Item = styled(ListItemButton)`
  padding-top: ${(props) => props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) => props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) =>
    props.collapsed
      ? props.theme.spacing(6)
      : props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-right: ${(props) =>
    props.collapsed
      ? props.theme.spacing(6)
      : props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  position: relative;
  transition: all 0.2s ease-in-out;

  svg {
    color: #666;
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
    transition: all 0.2s ease-in-out;
  }

  &:hover {
    background: #f0f7ff;
    color: #2196f3;
    border-left: 3px solid #2196f3;
    svg {
      color: #2196f3;
      opacity: 1;
    }
  }

  &.Mui-selected {
    background-color: ${(props) => rgba(props.theme.palette.primary.main, 0.08)};
    color: ${(props) => props.theme.palette.primary.main};
    border-left: 4px solid ${(props) => props.theme.palette.primary.main};
    padding-left: ${(props) => props.theme.spacing(props.depth && props.depth > 0 ? 13 : 7)};

    svg {
      color: ${(props) => props.theme.palette.primary.main};
      opacity: 1;
    }

    span {
      color: ${(props) => props.theme.palette.primary.main};
      font-weight: ${(props) => props.theme.typography.fontWeightBold};
    }

    &:hover {
      background-color: ${(props) => rgba(props.theme.palette.primary.main, 0.12)};
    }
  }
`;

const Title = styled(ListItemText)`
  margin: 0;
  span {
    color: #333;
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
    transition: color 0.2s ease-in-out;
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = (props) => {
  const {
    title,
    href,
    depth = 0,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
    collapsed,
  } = props;

  const location = useLocation();
  const [open, setOpen] = React.useState(openProp);
  const theme = useTheme();

  // Handle automatic opening of parent if child is active
  React.useEffect(() => {
    if (children && children.props && children.props.items) {
      const isChildActive = children.props.items.some((item) => location.pathname === item.href);
      if (isChildActive) {
        setOpen(true);
      }
    }
  }, [location.pathname, children]);

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  const isActive = location.pathname === href;

  if (children) {
    return (
      <React.Fragment>
        <Item depth={depth} collapsed={collapsed} onClick={handleToggle} theme={theme}>
          {Icon && iconGenerate(Icon, false)}
          {!collapsed && (
            <React.Fragment>
              <Title depth={depth} theme={theme}>
                {title}
                {badge && <Badge label={badge} theme={theme} />}
              </Title>
              {open ? <ExpandLessIcon theme={theme} /> : <ExpandMoreIcon theme={theme} />}
            </React.Fragment>
          )}
        </Item>
        <Collapse in={open && !collapsed}>{children}</Collapse>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Item
        depth={depth}
        collapsed={collapsed}
        component={CustomRouterLink}
        to={href}
        selected={isActive}
        theme={theme}
      >
        {Icon && iconGenerate(Icon, isActive)}
        {!collapsed && (
          <Title depth={depth} theme={theme}>
            {title}
            {badge && <Badge label={badge} theme={theme} />}
          </Title>
        )}
      </Item>
    </React.Fragment>
  );
};

export default SidebarNavListItem;
